import type { FC } from 'react'
import React, { memo, useMemo, useState, useEffect } from 'react'
import { Line } from '@nivo/line'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Typography } from '@homeviews/shared-ui'
import NoData from '../NoData'
import ChartLegend from '../ChartLegend'
import { formatRating, getChartTooltipDate, isEqual } from '@homeviews/utils'
import { chartTheme } from '../../helpers/layout'
import ChartTooltip from '../ChartTooltip'

interface IProps {
  data: any
  gridRef?: HTMLElement
  additionalBottomText?: string
}

const HistoryChart: FC<IProps> = memo(
  ({ data, additionalBottomText }) => {
    const [disabledDatasets, setDisableDatasets] = useState<string[]>([])
    const [filteredData, setFilteredData] = useState<any>(data)

    useEffect(() => {
      if (!filteredData) {
        return
      }

      let newFilteredData = [...data]

      newFilteredData = newFilteredData.filter((item: any) => {
        if (
          disabledDatasets.find(
            (filterValue: string) => filterValue === item?.id,
          )
        ) {
          return false
        }
        return true
      })

      setFilteredData(newFilteredData)
    }, [disabledDatasets, data])

    const calculatedChartValues: any = useMemo(
      () => calculateChartValueRange(),
      [filteredData, calculateChartValueRange, disabledDatasets],
    )

    const handleChartLegendClick = (id: string) => {
      let newDatasets = [...disabledDatasets]

      if (newDatasets.find((itemId: any) => itemId === id)) {
        newDatasets = newDatasets.filter((itemId: any) => itemId !== id)
      } else {
        newDatasets.push(id)
      }
      setDisableDatasets(newDatasets)
    }

    if (!data || !data?.length) {
      return <NoData />
    }

    function calculateChartValueRange() {
      let min = 5
      let max = 0

      if (!filteredData?.length) {
        return {
          min,
          max,
        }
      }

      filteredData.forEach((dataset: any) => {
        if (dataset?.data) {
          dataset.data.forEach((item: any) => {
            const itemValue = Number(item.y)

            if (itemValue < min) {
              min = itemValue
            }

            if (itemValue > max) {
              max = itemValue
            }
          })
        }
      })

      min = Math.floor(min / 0.5) * 0.5
      max = Math.ceil(max / 0.5) * 0.5

      min = min - 0.1
      max = max + 0.1

      if (min < 0) {
        min = 0
      }

      if (max > 5) {
        max = 5
      }

      return {
        min: Number(min.toFixed(1)),
        max: Number(max.toFixed(1)),
      }
    }

    const maxChartValue = calculatedChartValues.max
    const minChartValue = calculatedChartValues.min

    return (
      <>
        <div className="l-chartContainer">
          <div className="l-chartWrapper">
            <AutoSizer>
              {({ height, width }) => {
                return (
                  <Line
                    data={filteredData}
                    enableGridX={false}
                    theme={chartTheme}
                    height={height}
                    width={width}
                    xScale={{
                      type: 'time',
                      format: '%Y-%m-%d',
                    }}
                    colors={{ datum: 'color' }}
                    xFormat="time:%Y-%m-%d"
                    yScale={{
                      type: 'linear',
                      stacked: false,
                      min: minChartValue,
                      max: maxChartValue,
                    }}
                    axisLeft={{
                      tickSize: 0,
                      tickPadding: 10,
                      tickRotation: 0,
                      legendOffset: 12,
                    }}
                    axisBottom={{
                      format: '%d/%m/%y',
                      legendOffset: -12,
                      tickSize: 0,
                      tickPadding: 10,
                      tickValues: Math.floor(width / 90),
                    }}
                    pointColor={'white'}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    margin={{ top: 50, right: 30, bottom: 70, left: 40 }}
                    pointSize={7}
                    useMesh={true}
                    tooltip={(tooltip) => {
                      return (
                        <div>
                          <ChartTooltip>
                            <ChartLegend
                              direction="vertical"
                              data={[
                                {
                                  text: `${getChartTooltipDate(
                                    tooltip.point?.data?.x,
                                  )}: ${formatRating(
                                    tooltip.point?.data?.yFormatted,
                                  )}`,
                                  shapeColor: tooltip?.point?.serieColor,
                                },
                              ]}
                            />
                          </ChartTooltip>
                        </div>
                      )
                    }}
                  />
                )
              }}
            </AutoSizer>
          </div>
          <ChartLegend
            data={data.map((item) => ({
              id: item.id,
              text: item.id,
              shapeColor: item.color,
              disabled: !!disabledDatasets.find(
                (datasetItem: any) => datasetItem === item.id,
              ),
            }))}
            disabledDatasets={disabledDatasets}
            onClick={handleChartLegendClick}
          />
        </div>
        {additionalBottomText ? (
          <Typography className="l-chartContainer__additionalBottomText">
            {additionalBottomText}
          </Typography>
        ) : (
          ''
        )}
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default HistoryChart
